import { useCallback, useEffect, useMemo, useState } from "react";
import firebase from "../../../../database/firebase";
import DB_PATH from "../../../../database/DB_PATH";
import { Plan } from "../../../../types/Plan";
import { DayPlan } from "../../../../types/DayPlan";
import { DailyStep } from "../../../../types/DailyStep";

// The Plan -> Day -> Step selection cascade for the step-bound generators.
// Subscribes to PLAN_STEP once per plan (the whole plan's steps, which the
// history rail needs anyway) and derives the selected day's steps by
// filtering, instead of the old double subscription (per-day in the container
// plus per-plan in the shell).

const sortByPosition = <T extends { position?: number; title?: string }>(
  items: T[]
): T[] =>
  [...items].sort((a, b) => {
    const pa =
      typeof a.position === "number" && Number.isFinite(a.position)
        ? a.position
        : Number.MAX_SAFE_INTEGER;
    const pb =
      typeof b.position === "number" && Number.isFinite(b.position)
        ? b.position
        : Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return (a.title || "").localeCompare(b.title || "");
  });

export type PlanStepSelection = {
  plans: Plan[];
  dayPlans: DayPlan[];
  /** Every step in the selected plan (drives the history rail). */
  planSteps: DailyStep[];
  /** The selected day's steps, sorted (drives the step dropdown). */
  steps: DailyStep[];
  loadingPlans: boolean;
  loadingDayPlans: boolean;
  loadingSteps: boolean;
  selectedPlanId: string;
  selectedDayPlanId: string;
  selectedStepId: string;
  selectPlanId: (id: string) => void;
  selectDayPlanId: (id: string) => void;
  selectStepId: (id: string) => void;
  selectedPlan: Plan | null;
  selectedStep: DailyStep | null;
  /** Optimistic local patch of a step field (mirrors what generators save). */
  patchStepField: (stepId: string, field: string, value: any) => void;
};

export const usePlanStepSelection = (
  communityId?: string
): PlanStepSelection => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [dayPlans, setDayPlans] = useState<DayPlan[]>([]);
  const [loadingDayPlans, setLoadingDayPlans] = useState(false);
  const [planSteps, setPlanSteps] = useState<DailyStep[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);

  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedDayPlanId, setSelectedDayPlanId] = useState("");
  const [selectedStepId, setSelectedStepId] = useState("");

  // ── Load plans ──
  useEffect(() => {
    if (!communityId) {
      setPlans([]);
      setLoadingPlans(false);
      return;
    }
    setLoadingPlans(true);
    const unsubscribe = firebase
      .firestore()
      .collection(DB_PATH.PLAN)
      .where("communityId", "==", communityId)
      .onSnapshot(
        (snapshot) => {
          const next = snapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() } as Plan)
          );
          setPlans(sortByPosition(next));
          setLoadingPlans(false);
        },
        (err) => {
          console.error("Error loading plans for media studio:", err);
          setLoadingPlans(false);
        }
      );
    return () => unsubscribe();
  }, [communityId]);

  // ── Auto-select first plan ──
  useEffect(() => {
    if (!plans.length) {
      setSelectedPlanId("");
      return;
    }
    setSelectedPlanId((cur) => {
      if (!cur) return plans[0].id;
      return plans.some((p) => p.id === cur) ? cur : plans[0].id;
    });
  }, [plans]);

  // ── Load day plans when plan changes ──
  useEffect(() => {
    if (!selectedPlanId) {
      setDayPlans([]);
      setSelectedDayPlanId("");
      return;
    }
    setLoadingDayPlans(true);
    setSelectedDayPlanId("");
    const unsubscribe = firebase
      .firestore()
      .collection(DB_PATH.PLAN_DAY)
      .where("planID", "==", selectedPlanId)
      .onSnapshot(
        (snapshot) => {
          const next = snapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() } as DayPlan)
          );
          setDayPlans(sortByPosition(next));
          setLoadingDayPlans(false);
        },
        (err) => {
          console.error("Error loading day plans for media studio:", err);
          setLoadingDayPlans(false);
        }
      );
    return () => unsubscribe();
  }, [selectedPlanId]);

  // ── Auto-select first day plan ──
  useEffect(() => {
    if (!dayPlans.length) {
      setSelectedDayPlanId("");
      return;
    }
    setSelectedDayPlanId((cur) => {
      if (!cur) return dayPlans[0].id;
      return dayPlans.some((d) => d.id === cur) ? cur : dayPlans[0].id;
    });
  }, [dayPlans]);

  // ── Load ALL steps for the plan (single subscription) ──
  useEffect(() => {
    if (!selectedPlanId) {
      setPlanSteps([]);
      setSelectedStepId("");
      return;
    }
    setLoadingSteps(true);
    const unsubscribe = firebase
      .firestore()
      .collection(DB_PATH.PLAN_STEP)
      .where("planID", "==", selectedPlanId)
      .onSnapshot(
        (snapshot) => {
          const next = snapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() } as DailyStep)
          );
          setPlanSteps(next);
          setLoadingSteps(false);
        },
        (err) => {
          console.error("Error loading steps for media studio:", err);
          setLoadingSteps(false);
        }
      );
    return () => unsubscribe();
  }, [selectedPlanId]);

  // ── Selected day's steps, derived ──
  const steps = useMemo(
    () =>
      selectedDayPlanId
        ? sortByPosition(
            planSteps.filter((s) => s.dayPlanID === selectedDayPlanId)
          )
        : [],
    [planSteps, selectedDayPlanId]
  );

  // ── Auto-select first step of the day ──
  useEffect(() => {
    if (!steps.length) {
      setSelectedStepId("");
      return;
    }
    setSelectedStepId((cur) => {
      if (!cur) return steps[0].id;
      return steps.some((s) => s.id === cur) ? cur : steps[0].id;
    });
  }, [steps]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );
  const selectedStep = useMemo(
    () => planSteps.find((s) => s.id === selectedStepId) || null,
    [planSteps, selectedStepId]
  );

  const patchStepField = useCallback(
    (stepId: string, field: string, value: any) => {
      setPlanSteps((prev) =>
        prev.map((step) =>
          step.id === stepId ? ({ ...step, [field]: value } as DailyStep) : step
        )
      );
    },
    []
  );

  return {
    plans,
    dayPlans,
    planSteps,
    steps,
    loadingPlans,
    loadingDayPlans,
    loadingSteps,
    selectedPlanId,
    selectedDayPlanId,
    selectedStepId,
    selectPlanId: setSelectedPlanId,
    selectDayPlanId: setSelectedDayPlanId,
    selectStepId: setSelectedStepId,
    selectedPlan,
    selectedStep,
    patchStepField,
  };
};

export default usePlanStepSelection;
